import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Loader2, Home, User, DollarSign, CloudLightning, Shield, Sparkles, MapPin, Mic } from "lucide-react";
import { type PropertyEnrichment, getScoreColor, getScoreLabel } from "@/hooks/useEnrichment";

interface PropertyDetailSheetProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  enrichment: PropertyEnrichment | null;
  loading: boolean;
  address?: string;
  onKnock: () => void;
  onAskBridgette?: () => void;
}

export function PropertyDetailSheet({
  open,
  onOpenChange,
  enrichment,
  loading,
  address,
  onKnock,
  onAskBridgette,
}: PropertyDetailSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Home className="w-5 h-5" />
            Property Intel
          </SheetTitle>
          {(enrichment?.address || address) && (
            <p className="text-sm text-muted-foreground flex items-start gap-1.5">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {enrichment?.address || address}
            </p>
          )}
          {enrichment?.data_source === "regrid_public_record" && (
            <Badge variant="outline" className="w-fit gap-1 text-[10px] border-emerald-500/40 text-emerald-700 dark:text-emerald-400">
              <Shield className="w-3 h-3" /> Verified public record
            </Badge>
          )}
          {enrichment?.data_source === "ai_inferred" && (
            <Badge variant="outline" className="w-fit gap-1 text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-400">
              <Sparkles className="w-3 h-3" /> AI estimate
            </Badge>
          )}
        </SheetHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Bridgette is enriching this property…</p>
          </div>
        )}

        {!loading && enrichment && (
          <div className="space-y-5 mt-4">
            {/* Score hero */}
            <div
              className="rounded-xl p-4 text-white"
              style={{ background: `linear-gradient(135deg, ${getScoreColor(enrichment.opportunity_score)}, ${getScoreColor(enrichment.opportunity_score)}dd)` }}
            >
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide opacity-90">Opportunity Score</div>
                  <div className="text-4xl font-bold">{enrichment.opportunity_score}</div>
                </div>
                <Badge className="bg-white/25 hover:bg-white/30 text-white border-0">
                  {getScoreLabel(enrichment.opportunity_score)}
                </Badge>
              </div>
              {enrichment.score_reasoning && (
                <p className="text-xs mt-3 opacity-95 leading-relaxed">{enrichment.score_reasoning}</p>
              )}
            </div>

            {/* Hot factors */}
            {enrichment.hot_factors && enrichment.hot_factors.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Why this house
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {enrichment.hot_factors.map((f, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            <Section icon={Home} title="Property">
              <Row label="Sq ft" value={enrichment.est_sqft ? `${enrichment.est_sqft.toLocaleString()}` : "—"} />
              <Row label="Year built" value={enrichment.est_year_built ?? "—"} />
              <Row label="Roof squares" value={enrichment.est_roof_squares ? `${enrichment.est_roof_squares} sq` : "—"} />
              <Row label="Stories" value={enrichment.est_stories ?? "—"} />
              <Row label="Roof material" value={enrichment.roof_material_guess ?? "—"} />
              <Row label="Lot size" value={enrichment.lot_size_sqft ? `${enrichment.lot_size_sqft.toLocaleString()} sq ft` : "—"} />
            </Section>

            <Section icon={User} title="Owner">
              <Row label="Status" value={enrichment.owner_name ?? "—"} />
              <Row label="Owner-occupied" value={enrichment.owner_occupied === true ? "Yes" : enrichment.owner_occupied === false ? "No (rental)" : "—"} />
              <Row label="Tenure" value={enrichment.est_tenure_years ? `~${enrichment.est_tenure_years} years` : "—"} />
            </Section>

            <Section icon={DollarSign} title="Financial">
              <Row label="Home value" value={enrichment.est_home_value ? `$${enrichment.est_home_value.toLocaleString()}` : "—"} />
              <Row
                label="Equity"
                value={
                  enrichment.est_equity_bracket ? (
                    <Badge variant={enrichment.est_equity_bracket === "high" ? "default" : "secondary"}>
                      {enrichment.est_equity_bracket}
                    </Badge>
                  ) : "—"
                }
              />
              <Row label="Mortgage age" value={enrichment.mortgage_age_years_est ? `~${enrichment.mortgage_age_years_est} yrs` : "—"} />
            </Section>

            <Section icon={CloudLightning} title="Storm & Insurance">
              <Row label="Last storm" value={enrichment.last_storm_event_date ? `${enrichment.last_storm_event_date} (${enrichment.last_storm_event_type ?? "—"})` : "—"} />
              <Row label="Carrier guess" value={enrichment.insurance_carrier_guess ?? "—"} />
              <Row
                label="Claim likelihood"
                value={
                  enrichment.prior_claim_likelihood ? (
                    <Badge variant={enrichment.prior_claim_likelihood === "high" ? "default" : "secondary"}>
                      {enrichment.prior_claim_likelihood}
                    </Badge>
                  ) : "—"
                }
              />
            </Section>

            <div className="flex gap-2 sticky bottom-0 bg-background pt-3 border-t">
              <Button onClick={onKnock} className="flex-1 gap-1.5">
                <MapPin className="w-4 h-4" /> Knock this door
              </Button>
              {onAskBridgette && (
                <Button variant="outline" size="icon" onClick={onAskBridgette} title="Ask Bridgette">
                  <Mic className="w-4 h-4" />
                </Button>
              )}
            </div>

            <p className="text-[10px] text-muted-foreground text-center pb-2 flex items-center justify-center gap-1">
              <Shield className="w-3 h-3" /> AI-inferred estimates · upgrade to ATTOM for verified data
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}
